import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

const AuctionTimer = ({ endTime, size = 'default', showIcon = true, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [isUrgent, setIsUrgent] = useState(false);

  function calculateTimeLeft() {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;

    if (diff <= 0) {
      return { ended: true };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds, diff };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      // Set urgent if less than 1 hour remaining
      if (!newTimeLeft.ended && newTimeLeft.diff < 60 * 60 * 1000) {
        setIsUrgent(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime]);

  if (timeLeft.ended) {
    return (
      <div className={`flex items-center gap-1.5 text-red-600 dark:text-red-400 font-medium ${size === 'small' || compact ? 'text-xs' : 'text-sm'}`}>
        <AlertTriangle className={size === 'small' || compact ? 'w-3 h-3' : 'w-4 h-4'} />
        <span>Auction Ended</span>
      </div>
    );
  }

  // Compact display for sidebar
  if (compact) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
        isUrgent 
          ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800' 
          : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
      }`}>
        {isUrgent ? (
          <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
        ) : (
          <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        )}
        <div className="flex items-center gap-1 font-mono text-sm font-semibold">
          {timeLeft.days > 0 && (
            <span className={isUrgent ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}>
              {timeLeft.days}d
            </span>
          )}
          <span className={isUrgent ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}>
            {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
          </span>
        </div>
        <span className={`text-xs ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
          {isUrgent ? 'Ending soon!' : 'remaining'}
        </span>
      </div>
    );
  }

  // Compact display for listing cards
  if (size === 'small') {
    return (
      <div className={`flex items-center gap-1.5 font-medium ${
        isUrgent 
          ? 'text-red-600 dark:text-red-400' 
          : 'text-amber-600 dark:text-amber-400'
      }`}>
        {showIcon && <Clock className="w-3 h-3" />}
        <span className="text-xs">
          {timeLeft.days > 0 && `${timeLeft.days}d `}
          {timeLeft.hours}h {timeLeft.minutes}m
        </span>
      </div>
    );
  }

  // Full display for detail page
  return (
    <div 
      className={`rounded-2xl p-5 ${
        isUrgent 
          ? 'bg-gradient-to-r from-red-500 to-orange-500' 
          : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
      }`}
      data-testid="auction-timer"
    >
      <div className="flex items-center gap-2 mb-3">
        {isUrgent ? (
          <AlertTriangle className="w-5 h-5 text-white" />
        ) : (
          <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        )}
        <span className={`font-semibold ${isUrgent ? 'text-white' : 'text-foreground'}`}>
          {isUrgent ? 'Ending Soon!' : 'Time Remaining'}
        </span>
      </div>
      
      <div className="grid grid-cols-4 gap-2 text-center">
        <div className={`p-2 rounded-lg ${
          isUrgent 
            ? 'bg-white/20' 
            : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600'
        }`}>
          <div className={`text-2xl font-bold ${isUrgent ? 'text-white' : 'text-foreground'}`}>
            {String(timeLeft.days).padStart(2, '0')}
          </div>
          <div className={`text-xs ${isUrgent ? 'text-white/70' : 'text-muted-foreground'}`}>Days</div>
        </div>
        <div className={`p-2 rounded-lg ${
          isUrgent 
            ? 'bg-white/20' 
            : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600'
        }`}>
          <div className={`text-2xl font-bold ${isUrgent ? 'text-white' : 'text-foreground'}`}>
            {String(timeLeft.hours).padStart(2, '0')}
          </div>
          <div className={`text-xs ${isUrgent ? 'text-white/70' : 'text-muted-foreground'}`}>Hours</div>
        </div>
        <div className={`p-2 rounded-lg ${
          isUrgent 
            ? 'bg-white/20' 
            : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600'
        }`}>
          <div className={`text-2xl font-bold ${isUrgent ? 'text-white' : 'text-foreground'}`}>
            {String(timeLeft.minutes).padStart(2, '0')}
          </div>
          <div className={`text-xs ${isUrgent ? 'text-white/70' : 'text-muted-foreground'}`}>Mins</div>
        </div>
        <div className={`p-2 rounded-lg ${
          isUrgent 
            ? 'bg-white/20' 
            : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600'
        }`}>
          <div className={`text-2xl font-bold ${isUrgent ? 'text-white' : 'text-foreground'}`}>
            {String(timeLeft.seconds).padStart(2, '0')}
          </div>
          <div className={`text-xs ${isUrgent ? 'text-white/70' : 'text-muted-foreground'}`}>Secs</div>
        </div>
      </div>
    </div>
  );
};

export default AuctionTimer;
